import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 职业技能与实操技术 - 竖屏「任务清单」风格
 *
 * 参考 Notion/Linear/Things 任务管理 UI，极简 + 勾选动画 + 进度统计
 * 适合：学习路线、技能清单、面试准备、项目流程
 * 尺寸：1080x1920 竖屏
 */

interface CheckItem {
  text: string;
  tag?: string;
  tagColor?: string;
}

interface SkillsChecklistProps {
  listTitle: string;
  listIcon: string;
  description: string;
  items: CheckItem[];
  completionText: string;
  proTip: string;
}

const DEFAULT_PROPS: SkillsChecklistProps = {
  listTitle: '前端面试准备清单',
  listIcon: '📋',
  description: '系统复习路线，两周搞定高频考点',
  items: [
    { text: 'JavaScript 原型链与闭包', tag: '核心', tagColor: '#ef4444' },
    { text: 'CSS Flex/Grid 布局精通', tag: '基础', tagColor: '#3b82f6' },
    { text: 'React Hooks 原理与最佳实践', tag: '框架', tagColor: '#8b5cf6' },
    { text: 'HTTP 协议与浏览器渲染机制', tag: '网络', tagColor: '#f59e0b' },
    { text: '手写 Promise / 防抖节流', tag: '手撕', tagColor: '#ef4444' },
    { text: 'Webpack 构建优化', tag: '工程', tagColor: '#06b6d4' },
    { text: 'TypeScript 泛型与类型体操', tag: '进阶', tagColor: '#8b5cf6' },
    { text: '算法：排序 / 二叉树 / 动态规划', tag: '算法', tagColor: '#10b981' },
  ],
  completionText: '全部完成，自信出击！',
  proTip: '每天集中攻克 1-2 个专题，比零散刷题更有效',
};

export const SkillsChecklistTemplate: React.FC<Partial<SkillsChecklistProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Header
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Items
  const itemStart = 50;
  const itemInterval = 40;

  // Current check progress
  const checkedCount = Math.floor(
    interpolate(frame, [itemStart, itemStart + p.items.length * itemInterval], [0, p.items.length], { extrapolateRight: 'clamp' })
  );

  // Completion
  const allDone = checkedCount >= p.items.length;
  const completionStart = itemStart + p.items.length * itemInterval + 20;
  const completionOpacity = interpolate(frame, [completionStart, completionStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const completionScale = spring({
    frame: frame - completionStart,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 10, stiffness: 120, mass: 0.6 },
  });

  // Tip
  const tipStart = completionStart + 40;
  const tipOpacity = interpolate(frame, [tipStart, tipStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  const progressPercent = (checkedCount / p.items.length) * 100;

  return (
    <AbsoluteFill
      style={{
        background: '#fafafa',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          left: 56,
          right: 56,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -20}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: 16,
              background: '#111',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 30,
            }}
          >
            {p.listIcon}
          </div>
          <div>
            <h1 style={{ fontSize: 50, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{p.listTitle}</h1>
            <p style={{ fontSize: 36, color: '#94a3b8', margin: '4px 0 0' }}>{p.description}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 60 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 34, color: '#94a3b8', fontWeight: 500 }}>
              {checkedCount}/{p.items.length} 已完成
            </span>
            <span style={{ fontSize: 34, color: allDone ? '#10b981' : '#64748b', fontWeight: 700 }}>
              {Math.round(progressPercent)}%
            </span>
          </div>
          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${progressPercent}%`,
                background: allDone
                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                  : 'linear-gradient(90deg, #111, #374151)',
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      </div>

      {/* Checklist items */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 56,
          right: 56,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {p.items.map((item, i) => {
          const start = itemStart + i * itemInterval;
          const cardProgress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 16, stiffness: 130, mass: 0.5 },
          });
          const isChecked = i < checkedCount;

          // Check animation
          const checkScale = spring({
            frame: frame - (start + 20),
            fps,
            from: 0,
            to: 1,
            config: { damping: 8, stiffness: 200, mass: 0.4 },
          });

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '18px 20px',
                background: 'white',
                borderRadius: 16,
                border: '1px solid #f1f5f9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                opacity: cardProgress,
                transform: `translateX(${(1 - cardProgress) * 40}px)`,
              }}
            >
              {/* Checkbox */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  border: isChecked ? 'none' : '2px solid #d1d5db',
                  background: isChecked ? '#111' : 'transparent',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0,
                  transform: `scale(${isChecked ? checkScale : 1})`,
                }}
              >
                {isChecked && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>

              {/* Text */}
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontSize: 34,
                    fontWeight: 600,
                    color: isChecked ? '#94a3b8' : '#1a1a1a',
                    textDecoration: isChecked ? 'line-through' : 'none',
                  }}
                >
                  {item.text}
                </span>
              </div>

              {/* Tag */}
              {item.tag && (
                <div
                  style={{
                    padding: '4px 12px',
                    borderRadius: 8,
                    background: `${item.tagColor || '#6366f1'}15`,
                    fontSize: 28,
                    fontWeight: 600,
                    color: item.tagColor || '#6366f1',
                    flexShrink: 0,
                  }}
                >
                  {item.tag}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Completion celebration */}
      {allDone && (
        <div
          style={{
            position: 'absolute',
            bottom: 240,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: completionOpacity,
            transform: `scale(${completionScale})`,
          }}
        >
          <div style={{ fontSize: 56 }}>🎉</div>
          <div style={{ fontSize: 34, fontWeight: 800, marginTop: 12, color: '#10b981' }}>
            {p.completionText}
          </div>
        </div>
      )}

      {/* Pro tip */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 56,
          right: 56,
          opacity: tipOpacity,
        }}
      >
        <div
          style={{
            padding: '18px 24px',
            background: '#fffbeb',
            borderRadius: 14,
            border: '1px solid #fde68a',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span style={{ fontSize: 34 }}>💡</span>
          <span style={{ fontSize: 34, color: '#92400e', lineHeight: 1.5 }}>{p.proTip}</span>
        </div>
      </div>

      {/* Bottom progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, tipStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: '#111',
        }}
      />
    </AbsoluteFill>
  );
};
